import { useNavigate, Link } from "react-router-dom";
import {
  BookOpen,
  Briefcase,
  Wrench,
  LogOut,
} from "lucide-react";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useAuth } from "../hooks/useAuth";
import { showConfirm } from "../utils/confirm";
import { ROUTES } from "../config/constants";

export default function Portal() {
  const navigate = useNavigate();
  const { userName } = useCurrentUser();
  const { forceLogout } = useAuth();

  function handleLogout() {
    showConfirm({
      title: "Konfirmasi Logout",
      message: "Apakah Anda yakin ingin keluar dari aplikasi?",
      confirmLabel: "Ya, Logout",
      cancelLabel: "Batal",
      onConfirm: () => {
        forceLogout();
        navigate(ROUTES.LOGIN);
      },
    });
  }

  const cards = [
    {
      title: "Knowledge Tools",
      desc: "Dokumentasi pembelajaran di proyek WIKA",
      icon: <BookOpen size={34} className="text-blue-500" />,
      glow: "from-blue-500/10 to-cyan-400/5",
      border: "border-blue-100",
      link: "/portalknowledge",
    },
    {
      title: "Digitalization Management",
      desc: "Manajemen pengembangan, distribusi dan penggunaan aplikasi digital",
      icon: <Briefcase size={34} className="text-emerald-500" />,
      glow: "from-emerald-500/10 to-teal-400/5",
      border: "border-emerald-100",
      link: "",
    },
    {
      title: "Engineering Tools",
      desc: "Tools untuk support pekerjaan terkait engineering",
      icon: <Wrench size={34} className="text-orange-500" />,
      glow: "from-orange-500/10 to-amber-400/5",
      border: "border-orange-100",
      link: "/engineeringtools",
    },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden bg-white px-6 py-8 md:px-10">
      {/* Background Glow */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-[-120px] left-[-80px] w-[380px] h-[380px] bg-blue-200/40 rounded-full blur-3xl animate-pulse" />

        <div
          className="absolute bottom-[-120px] right-[-80px] w-[420px] h-[420px] bg-emerald-200/40 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: "6s" }}
        />

        <div
          className="absolute top-[40%] left-[45%] w-[260px] h-[260px] bg-cyan-200/30 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: "8s" }}
        />

        {/* Grid Effect */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />
      </div>

      <div className="relative max-w-7xl mx-auto z-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 mb-12">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gray-200 bg-white/70 backdrop-blur-md mb-4 shadow-sm">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[11px] tracking-wider text-gray-600 uppercase">
                WIKA Digital Platform
              </span>
            </div>

            <h1 className="text-2xl md:text-4xl font-semibold text-gray-900 leading-tight max-w-3xl">
              Engineering Strategic Digital Platform
            </h1>

            <p className="text-gray-500 mt-3 text-sm">
              Selamat datang kembali,{" "}
              <span className="text-gray-900 font-medium">
                {userName}
              </span>
            </p>
          </div>

          <button
            onClick={handleLogout}
            className="group flex items-center gap-2 px-4 py-2.5 text-sm text-red-500 bg-white/80 border border-red-100 backdrop-blur-xl rounded-2xl hover:bg-red-50 transition-all duration-300 shadow-sm"
          >
            <LogOut
              size={16}
              className="group-hover:rotate-6 transition-transform"
            />
            <span>Logout</span>
          </button>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {cards.map((item, index) => (
            <Link
              key={index}
              to={item.link}
              className={`group relative overflow-hidden rounded-3xl border ${item.border} bg-white/70 backdrop-blur-xl p-7 min-h-[280px] transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl shadow-lg`}
            >
              {/* Glow Effect */}
              <div
                className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-500 bg-gradient-to-br ${item.glow}`}
              />

              {/* Glass Overlay */}
              <div className="absolute inset-0 bg-white/40" />

              <div className="relative z-10 flex flex-col h-full">
                {/* Icon */}
                <div className="w-16 h-16 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 shadow-sm">
                  {item.icon}
                </div>

                {/* Title */}
                <h2 className="text-lg font-semibold text-gray-900 mb-3 leading-snug">
                  {item.title}
                </h2>

                {/* Desc */}
                <p className="text-sm text-gray-500 leading-relaxed">
                  {item.desc}
                </p>

                {/* Bottom */}
                <div className="mt-auto pt-8">
                  <div className="inline-flex items-center gap-2 text-xs text-gray-600 group-hover:text-gray-900 transition">
                    <span>Open Module</span>

                    <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                      →
                    </div>
                  </div>
                </div>
              </div>

              {/* Border Glow */}
              <div className="absolute inset-0 rounded-3xl border border-white/40 group-hover:border-gray-200 transition-all duration-500" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
